import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import Timestamp from '../../../components/Timestamp';
import EntityNotesPanel from './EntityNotesPanel';
import TabEmpty from './TabEmpty';
import TabLoading from './TabLoading';

const REGENERATE_CONFIRM_MESSAGE =
    'Regenerating will create a new AI summary using your edits as expert guidance. '
    + 'The new summary will not preserve your text verbatim. Continue?';

function formatSourceCounts(sourceCounts) {
    if (!sourceCounts) return null;
    const parts = [];
    if (typeof sourceCounts.fintel === 'number') {
        parts.push(`${sourceCounts.fintel} FINTEL`);
    }
    if (typeof sourceCounts.draft === 'number') {
        parts.push(`${sourceCounts.draft} draft`);
    }
    if (typeof sourceCounts.corpus === 'number') {
        parts.push(`${sourceCounts.corpus} corpus`);
    }
    if (typeof sourceCounts.userSources === 'number') {
        parts.push(`${sourceCounts.userSources} user sources`);
    }
    if (typeof sourceCounts.notes === 'number' && sourceCounts.notes > 0) {
        parts.push(`${sourceCounts.notes} notes`);
    }
    return parts.length ? parts.join(' · ') : null;
}

/**
 * Summary tab — AI-generated entity intelligence briefing with optional notes thread.
 */
const SummaryTab = ({
    summary,
    loading = false,
    regenerating = false,
    saving = false,
    error = null,
    cached = false,
    stale = false,
    manuallyEdited = false,
    generatedAt = null,
    kbLastUpdatedAt = null,
    pinEnabled = false,
    kbLastUpdatedPinned = false,
    onToggleKbLastUpdatedPin,
    sourceCounts = null,
    onRegenerate,
    canEdit = false,
    onSave,
    notes,
    notesLoading = false,
    notesError = null,
    notesCanPost = false,
    onPostNote,
    onUpdateNote,
    onDeleteNote,
    notesSaving = false,
    notesMutationError = null,
}) => {
    const [editing, setEditing] = useState(false);
    const [editDraft, setEditDraft] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const editBaselineRef = useRef('');

    useEffect(() => {
        if (!editing) {
            setEditDraft(summary || '');
        }
    }, [summary, editing]);

    const handleEditStart = useCallback(() => {
        editBaselineRef.current = summary || '';
        setEditDraft(summary || '');
        setEditing(true);
    }, [summary]);

    const handleEditCancel = useCallback(() => {
        setEditDraft(editBaselineRef.current);
        setEditing(false);
    }, []);

    const handleEditSave = useCallback(async () => {
        const text = editDraft.trim();
        if (!text || !onSave) return;
        await onSave(text);
        setEditing(false);
    }, [editDraft, onSave]);

    const startRefresh = useCallback(() => {
        if (!onRegenerate) return;
        if (manuallyEdited) {
            onRegenerate(summary || editDraft || '');
            return;
        }
        onRegenerate();
    }, [editDraft, manuallyEdited, onRegenerate, summary]);

    const handleRegenerateClick = useCallback(() => {
        if (manuallyEdited) {
            setConfirmOpen(true);
            return;
        }
        startRefresh();
    }, [manuallyEdited, startRefresh]);

    const handleConfirmRegenerate = useCallback(() => {
        setConfirmOpen(false);
        startRefresh();
    }, [startRefresh]);

    const handlePostNote = useCallback(async (body) => {
        if (!onPostNote) return undefined;
        const result = await onPostNote(body);
        startRefresh();
        return result;
    }, [onPostNote, startRefresh]);

    const handleUpdateNote = useCallback(async (noteId, body) => {
        if (!onUpdateNote) return undefined;
        const result = await onUpdateNote(noteId, body);
        startRefresh();
        return result;
    }, [onUpdateNote, startRefresh]);

    const handleDeleteNote = useCallback(async (noteId) => {
        if (!onDeleteNote) return undefined;
        const result = await onDeleteNote(noteId);
        startRefresh();
        return result;
    }, [onDeleteNote, startRefresh]);

    if (loading && !summary && !editing) {
        return <TabLoading/>;
    }

    const countsLabel = formatSourceCounts(sourceCounts);
    const displaySummary = editing ? editDraft : summary;
    const showEmpty = !loading && !displaySummary?.trim() && !error && !editing;

    return (
        <>
            <Stack spacing={2} sx={{p: 2, minHeight: 280}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack spacing={0.25}>
                        {kbLastUpdatedAt || pinEnabled ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="caption" color="text.secondary">
                                    Last Information Collected{' '}
                                    <Timestamp component="span" value={kbLastUpdatedAt}/>
                                </Typography>
                                {pinEnabled ? (
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => onToggleKbLastUpdatedPin?.(!kbLastUpdatedPinned)}
                                    >
                                        {kbLastUpdatedPinned ? 'Unpin from card' : 'Pin to card'}
                                    </Button>
                                ) : null}
                            </Stack>
                        ) : null}
                        {generatedAt ? (
                            <Typography variant="caption" color="text.secondary">
                                {cached && !stale ? 'Cached · ' : ''}
                                {manuallyEdited ? 'Edited · ' : ''}
                                Generated{' '}
                                <Timestamp component="span" value={generatedAt}/>
                            </Typography>
                        ) : null}
                        {countsLabel ? (
                            <Typography variant="caption" color="text.secondary">
                                Sources: {countsLabel}
                            </Typography>
                        ) : null}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        {canEdit && onSave && !editing ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon/>}
                                disabled={loading || regenerating || saving || notesSaving}
                                onClick={handleEditStart}
                            >
                                Edit
                            </Button>
                        ) : null}
                        {onRegenerate ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RefreshIcon/>}
                                disabled={loading || regenerating || saving || editing || notesSaving}
                                onClick={handleRegenerateClick}
                            >
                                {regenerating ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        ) : null}
                    </Stack>
                </Stack>

                {error ? (
                    <Alert severity="error">
                        {error.message || 'Failed to generate summary.'}
                    </Alert>
                ) : null}

                {stale && summary && !editing ? (
                    <Alert severity="info" sx={{py: 0.5}}>
                        Source data changed; refresh to update this summary.
                    </Alert>
                ) : null}

                {showEmpty ? (
                    <TabEmpty message="No summary available for this entity yet."/>
                ) : null}

                {editing ? (
                    <Stack spacing={1}>
                        <TextField
                            multiline
                            minRows={6}
                            fullWidth
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            disabled={saving}
                            inputProps={{'aria-label': 'Edit intelligence summary'}}
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={handleEditCancel} disabled={saving}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                disabled={saving || !editDraft.trim()}
                                onClick={handleEditSave}
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </Button>
                        </Stack>
                    </Stack>
                ) : null}

                {!editing && displaySummary?.trim() ? (
                    <Box>
                        {loading || regenerating ? (
                            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
                                {regenerating ? 'Regenerating summary…' : 'Updating summary…'}
                            </Typography>
                        ) : null}
                        <Typography
                            variant="body1"
                            sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6}}
                        >
                            {displaySummary}
                        </Typography>
                    </Box>
                ) : null}

                {notes !== undefined ? (
                    <EntityNotesPanel
                        embedded
                        notes={notes}
                        loading={notesLoading}
                        error={notesError}
                        canPost={notesCanPost}
                        onPost={onPostNote ? handlePostNote : undefined}
                        onUpdate={onUpdateNote ? handleUpdateNote : undefined}
                        onDelete={onDeleteNote ? handleDeleteNote : undefined}
                        saving={notesSaving}
                        postError={notesMutationError}
                    />
                ) : null}
            </Stack>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Regenerate summary?</DialogTitle>
                <DialogContent>
                    <DialogContentText>{REGENERATE_CONFIRM_MESSAGE}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmRegenerate} autoFocus>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SummaryTab;
